import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Search } from "lucide-react";
import nationalitiesData from "@/data/nationalities.json";
import type { Lang } from "@/lib/types";
import { HelpMeButton } from "./HelpMeButton";

type Nat = { code: string; fr: string; ar?: string; darija?: string };

const NATIONALITIES: { priority: Nat[]; rest: Nat[] } = nationalitiesData as { priority: Nat[]; rest: Nat[] };

export interface OptionLike {
  value: string;
  i18nKey: string;
  exclusive?: boolean;
}

export type QuestionType = "radio" | "multi_checkbox" | "dropdown_searchable";

export interface QuestionCardProps {
  questionId: string;
  type: QuestionType;
  i18nLabelKey: string;
  i18nHelpKey?: string;
  options?: OptionLike[];
  helpable?: boolean;
  value: string | string[];
  onChange: (value: string | string[]) => void;
  lang: Lang;
}

export function QuestionCard(props: QuestionCardProps) {
  const { t } = useTranslation();
  const label = t(`${props.i18nLabelKey}._label`);
  const help = props.i18nHelpKey ? (t(`${props.i18nHelpKey}._help`, { defaultValue: "" }) as string) : "";

  return (
    <div className="card md:max-w-wizard">
      <div className="flex items-start justify-between gap-4">
        <h2 className="font-display text-h3 text-ink">{label}</h2>
        {props.helpable && props.options && props.type !== "dropdown_searchable" && (
          <HelpMeButton
            lang={props.lang}
            questionId={props.questionId}
            questionLabel={label}
            options={props.options.map((o) => ({ value: o.value, label: t(o.i18nKey) }))}
            onSuggestionPicked={(v) => {
              if (props.type === "radio") props.onChange(v);
              else if (props.type === "multi_checkbox") props.onChange([v]);
            }}
          />
        )}
      </div>
      {help && <p className="mt-2 text-caption text-muted">{help}</p>}

      <div className="mt-6">
        {props.type === "radio" && props.options && (
          <RadioGroup
            options={props.options}
            value={typeof props.value === "string" ? props.value : ""}
            onChange={props.onChange}
          />
        )}
        {props.type === "multi_checkbox" && props.options && (
          <MultiCheckbox
            options={props.options}
            value={Array.isArray(props.value) ? props.value : []}
            onChange={props.onChange}
          />
        )}
        {props.type === "dropdown_searchable" && (
          <NationalityDropdown
            value={typeof props.value === "string" ? props.value : ""}
            onChange={props.onChange}
            placeholder={t(`${props.i18nLabelKey}._placeholder`)}
            lang={props.lang}
          />
        )}
      </div>
    </div>
  );
}

function RadioGroup({
  options,
  value,
  onChange,
}: {
  options: OptionLike[];
  value: string;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2" role="radiogroup">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            type="button"
            key={opt.value}
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            className={"question-option " + (active ? "question-option-active" : "")}
          >
            <span
              className={
                "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-[1.5px] " +
                (active ? "border-signal bg-signal" : "border-muted bg-paper")
              }
              aria-hidden
            >
              {active && <Check size={12} strokeWidth={3} className="text-paper" />}
            </span>
            <span className="text-body">{t(opt.i18nKey)}</span>
          </button>
        );
      })}
    </div>
  );
}

function MultiCheckbox({
  options,
  value,
  onChange,
}: {
  options: OptionLike[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const { t } = useTranslation();

  function toggle(opt: OptionLike) {
    const isChecked = value.includes(opt.value);
    if (isChecked) {
      const next = value.filter((v) => v !== opt.value);
      onChange(next.length ? next : [defaultExclusive(options)]);
      return;
    }
    if (opt.exclusive) {
      onChange([opt.value]);
      return;
    }
    // Selecting a non-exclusive option: drop any exclusive ones
    const exclusiveValues = options.filter((o) => o.exclusive).map((o) => o.value);
    const cleaned = value.filter((v) => !exclusiveValues.includes(v));
    onChange([...cleaned, opt.value]);
  }

  return (
    <div className="flex flex-col gap-2" role="group">
      {options.map((opt) => {
        const active = value.includes(opt.value);
        return (
          <button
            type="button"
            key={opt.value}
            role="checkbox"
            aria-checked={active}
            onClick={() => toggle(opt)}
            className={"question-option " + (active ? "question-option-active" : "")}
          >
            <span
              className={
                "flex h-5 w-5 shrink-0 items-center justify-center rounded border-[1.5px] " +
                (active ? "border-signal bg-signal" : "border-muted bg-paper")
              }
              aria-hidden
            >
              {active && <Check size={12} strokeWidth={3} className="text-paper" />}
            </span>
            <span className="text-body">{t(opt.i18nKey)}</span>
          </button>
        );
      })}
    </div>
  );
}

function defaultExclusive(options: OptionLike[]): string {
  return options.find((o) => o.exclusive)?.value ?? options[0].value;
}

function NationalityDropdown({
  value,
  onChange,
  placeholder,
  lang,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  lang: Lang;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const allOptions: Nat[] = useMemo(
    () => [...NATIONALITIES.priority, ...NATIONALITIES.rest],
    [],
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return null;
    const q = query.toLowerCase().trim();
    return allOptions.filter(
      (n) =>
        n.fr.toLowerCase().includes(q) ||
        n.code.toLowerCase().includes(q) ||
        (n.darija && n.darija.toLowerCase().includes(q)),
    );
  }, [query, allOptions]);

  const selected = allOptions.find((n) => n.code === value);
  const displayLabel = (n: Nat) => (lang === "darija" && n.darija ? n.darija : n.fr);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="input flex items-center justify-between text-left"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selected ? "text-ink" : "text-muted"}>
          {selected ? displayLabel(selected) : placeholder}
        </span>
        <ChevronDown size={18} strokeWidth={1.75} className="text-muted" />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-30 mt-2 max-h-72 overflow-hidden rounded-card border border-border bg-paper shadow-result">
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Search size={16} strokeWidth={1.75} className="text-muted" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="w-full bg-transparent text-body outline-none placeholder:text-muted"
            />
          </div>
          <ul role="listbox" className="max-h-56 overflow-y-auto">
            {(filtered ?? allOptions).map((n) => {
              const isPriority = NATIONALITIES.priority.some((p) => p.code === n.code);
              const isSelected = value === n.code;
              return (
                <li key={n.code}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(n.code);
                      setOpen(false);
                      setQuery("");
                    }}
                    className={
                      "flex w-full items-center justify-between px-3 py-2.5 text-left text-body transition-colors " +
                      (isSelected ? "bg-signal/10 text-ink" : "text-ink hover:bg-cream")
                    }
                    role="option"
                    aria-selected={isSelected}
                  >
                    <span>{displayLabel(n)}</span>
                    {isPriority && !filtered && (
                      <span className="ml-3 rounded-pill bg-accent-warm/20 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-accent-warm">
                        Top
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
            {filtered && filtered.length === 0 && (
              <li className="px-3 py-4 text-caption text-muted">Aucun résultat.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
